/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { 
    fetchCollectionForSale, 
    generateVfmLink, 
    calculateRecommendedPrice,
    fetchPriceHistory,
    sellSessionStorage
} from '../services';
import { formatPrice } from '../processors';
import LoadingInline from './loading-inline';
import SlidePanel from './slide-panel';
import CollectionParameters from './collection-parameters';
import { BGG_BASE_URL, BGG_MARKETPLACE_URL, PRICE_TYPES } from '../constants';
import { AiOutlineControl, AiOutlineInfoCircle, AiOutlineThunderbolt, AiOutlineLink, AiOutlineCopy, AiOutlineSetting } from 'react-icons/ai';
import { useContext } from 'react';
import { GlobalStateContext } from '../GlobalState';

/**
 * Seller List Component
 * 
 * Allows users to:
 * 1. Load their BGG collection by username
 * 2. Automatically get price recommendations based on market averages
 * 3. Generate Virtual Flea Market (VFM) add links
 */
const SellerList = () => {
    // Condition options with descriptions
    const CONDITION_OPTIONS = [
        { value: 'New', label: 'New – A brand new unused, unopened, and undamaged item in perfect condition. The original packaging and all materials are in brand new condition.' },
        { value: 'Like New', label: 'Like New – Item just removed from shrink wrap. No wear and tear, all facets are intact.' },
        { value: 'Very Good', label: 'Very Good – Very minimal wear and tear. All materials are present. You would give this item to a friend as a gift.' },
        { value: 'Good', label: 'Good – Minor damage to the box and/or its contents. All materials are present. Item may have been used once or twice.' },
        { value: 'Acceptable', label: 'Acceptable – Some damage to the box, but the item is still intact. Possible split corner(s) on the box. Maybe missing a non-crucial piece. Possibly missing rules/instructions, but are readily available on the web. Scuffing on the item.' },
        { value: 'Unacceptable', label: 'Unacceptable – Major damage to box and its parts. Possibly missing several important pieces. Broken or missing board/box. No rules/instructions, and they are not readily available. Do not sell this item on the marketplace.' }
    ];

    // Form inputs
    const [username, setUsername] = useState(() => sellSessionStorage.loadUsername());
    const [geeklistId, setGeeklistId] = useState(() => sellSessionStorage.loadGeeklistId());
    const [pricePercentage, setPricePercentage] = useState(() => sellSessionStorage.loadPricePercentage());
    
    // Price rounding settings
    const [roundingMode, setRoundingMode] = useState(() => sellSessionStorage.loadRoundingMode());
    const [roundingIncrement, setRoundingIncrement] = useState(() => sellSessionStorage.loadRoundingIncrement());
    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(() => sellSessionStorage.loadIsConfigPanelOpen());
    const [isActionsPanelOpen, setIsActionsPanelOpen] = useState(() => sellSessionStorage.loadIsActionsPanelOpen());
    const [isCollectionParamsOpen, setIsCollectionParamsOpen] = useState(() => sellSessionStorage.loadIsCollectionParamsOpen());
    
    // Default price type for all items
    const [defaultPriceType, setDefaultPriceType] = useState(() => sellSessionStorage.loadDefaultPriceType());
    const [defaultBinMultiplier, setDefaultBinMultiplier] = useState(() => sellSessionStorage.loadDefaultBinMultiplier());
    
    // Global BBCode to include in all VFM links
    const [globalBbCode, setGlobalBbCode] = useState(() => sellSessionStorage.loadGlobalBbCode() || '');
    
    // Collection parameters
    const [collectionParams, setCollectionParams] = useState(() => sellSessionStorage.loadCollectionParams());
    
    // Data state
    const [sellerItems, setSellerItems] = useState(() => sellSessionStorage.loadSellerItems());
    const [error, setError] = useState(null);
    
    // Loading states
    const [isLoadingCollection, setIsLoadingCollection] = useState(() => sellSessionStorage.loadIsLoadingCollection());
    const [loadingMessage, setLoadingMessage] = useState(() => sellSessionStorage.loadLoadingMessage());
    const [isLoadingPrices, setIsLoadingPrices] = useState(() => sellSessionStorage.loadIsLoadingPrices());
    const [priceProgress, setPriceProgress] = useState(() => sellSessionStorage.loadPriceProgress());
    
    // Grid reference
    const gridRef = useRef(null);

    // Get GlobalState context for row count
    const { setSellRowCount } = useContext(GlobalStateContext);
    
    // Update GlobalState row count when local row count changes
    useEffect(() => {
        setSellRowCount(sellerItems.length);
    }, [sellerItems.length, setSellRowCount]);

    // Initialize row count when component mounts
    useEffect(() => {
        setSellRowCount(sellerItems.length);
    }, [setSellRowCount, sellerItems.length]);

    // Round price based on current settings
    const roundPrice = useCallback((price) => {
        if (price === null || price === undefined || price === 0) return price;
        if (roundingMode === 'none') return price;
        
        const increment = roundingIncrement;
        let rounded;
        
        switch (roundingMode) {
            case 'up':
                rounded = Math.ceil(price / increment) * increment;
                break;
            case 'down':
                rounded = Math.floor(price / increment) * increment;
                break;
            case 'nearest':
            default:
                rounded = Math.round(price / increment) * increment;
                break;
        }
        
        // Handle floating point precision
        return Math.round(rounded * 100) / 100;
    }, [roundingMode, roundingIncrement]);

    // Load collection from BGG
    const handleLoadCollection = async () => {
        if (!username.trim()) {
            setError('Please enter a BGG username');
            return;
        }
        
        setIsLoadingCollection(true);
        setError(null);
        setLoadingMessage('Loading collection...');
        setSellerItems([]);
        
        // Save current state before loading
        sellSessionStorage.saveUsername(username);
        sellSessionStorage.saveCollectionParams(collectionParams);
        sellSessionStorage.saveIsLoadingCollection(true);
        sellSessionStorage.saveLoadingMessage('Loading collection...');
        
        const result = await fetchCollectionForSale(username.trim(), { 
            collectionParams: collectionParams 
        });
        
        if (result.error) {
            setError(result.error);
            setIsLoadingCollection(false);
            sellSessionStorage.saveIsLoadingCollection(false);
            return;
        }
        
        setIsLoadingCollection(false);
        sellSessionStorage.saveIsLoadingCollection(false);
        
        // Initialize seller items with collection data
        const initialSellerItems = result.items.map(item => ({
            id: item.id,
            name: item.name,
            versionName: item.versionName || '',
            versionId: item.versionId,
            imageId: item.imageId,
            thumbnail: item.thumbnail,
            yearPublished: item.yearPublished,
            averagePrice: null,
            calculatedPrice: null,
            userPrice: null,
            binPrice: null,
            priceType: defaultPriceType,
            condition: 'Very Good',
            conditionComments: '',
            customBody: '',
            vfmLink: null,
            isLoadingPrice: false
        }));
        
        setSellerItems(initialSellerItems);
        sellSessionStorage.saveSellerItems(initialSellerItems);
    };

    // Fetch price history for all items
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleLoadPrices = useCallback(async () => {
        if (sellerItems.length === 0) return;
        
        setIsLoadingPrices(true);
        const totalItems = sellerItems.length;
        setPriceProgress({ current: 0, total: totalItems });
        
        const updatedItems = [...sellerItems];
        
        for (let i = 0; i < updatedItems.length; i++) {
            const item = updatedItems[i];
            
            try {
                const priceResult = await fetchPriceHistory(item.id);
                const avgPrice = priceResult.averagePrice || 0;
                const calculatedPrice = calculateRecommendedPrice(avgPrice, pricePercentage);
                const finalPrice = roundPrice(calculatedPrice);
                const binPrice = (item.priceType === PRICE_TYPES.STARTING_BID_WITH_BIN || item.priceType === PRICE_TYPES.SOFT_RESERVE_WITH_BIN) ? roundPrice(finalPrice * defaultBinMultiplier) : null;
                
                updatedItems[i] = {
                    ...item,
                    averagePrice: avgPrice,
                    calculatedPrice: calculatedPrice,
                    userPrice: finalPrice,
                    binPrice: binPrice,
                    isLoadingPrice: false
                };
                
                setSellerItems([...updatedItems]);
            } catch (error) {
                console.error(`Error fetching price for ${item.name}:`, error);
                updatedItems[i] = {
                    ...item,
                    averagePrice: 0,
                    calculatedPrice: 0,
                    userPrice: 0,
                    binPrice: null,
                    isLoadingPrice: false
                };
            }
            
            setPriceProgress({ current: i + 1, total: totalItems });
        }
        
        setIsLoadingPrices(false);
    }, [sellerItems, pricePercentage, roundPrice, defaultBinMultiplier]);

    // Helper function to build complete BBCode body for an item listing
    const buildItemBody = useCallback((item) => {
        let bodyParts = [];
        
        // Add version info
        if (item.versionName) {
            bodyParts.push(`[B]Version:[/B] ${item.versionName}`);
        }
        
        // Add condition
        if (item.condition) {
            let conditionText = `[B]Condition:[/B] ${item.condition}`;
            if (item.conditionComments) {
                conditionText += ` - ${item.conditionComments}`;
            }
            bodyParts.push(conditionText);
        }
                
        // Add price based on price type
        if (item.userPrice && item.userPrice > 0) {
            if (item.priceType === 'FP') {
                bodyParts.push(`[B]FP:[/B] $${item.userPrice.toFixed(2)}`);
            } else if (item.priceType === 'SB') {
                bodyParts.push(`[B]SB:[/B] $${item.userPrice.toFixed(2)}`);
            } else if (item.priceType === 'SB+BIN') {
                bodyParts.push(`[B]SB:[/B] $${item.userPrice.toFixed(2)}`);
                if (item.binPrice && item.binPrice > item.userPrice) {
                    bodyParts.push(`[B]BIN:[/B] $${item.binPrice.toFixed(2)}`);
                }
            } else if (item.priceType === 'SR') {
                bodyParts.push(`[B]SR:[/B] $${item.userPrice.toFixed(2)}`);
            } else if (item.priceType === 'SR+BIN') {
                bodyParts.push(`[B]SR:[/B] $${item.userPrice.toFixed(2)}`);
                if (item.binPrice && item.binPrice > item.userPrice) {
                    bodyParts.push(`[B]BIN:[/B] $${item.binPrice.toFixed(2)}`);
                }
            } else if (item.priceType === 'BIN') {
                bodyParts.push(`[B]BIN:[/B] $${item.userPrice.toFixed(2)}`);
            }
        }
        
        // Add custom body
        if (item.customBody) {
            bodyParts.push(item.customBody);
        }
        
        // Add global BBCode
        if (globalBbCode) {
            bodyParts.push('\n'.concat(globalBbCode));
        }
        
        return bodyParts.join('\n');
    }, [globalBbCode]);

    // Helper function to generate VFM link for a single item
    const generateItemVfmLink = useCallback((item, listGeeklistId) => {
        const combinedBody = buildItemBody(item);
        return generateVfmLink({
            geeklistId: listGeeklistId.trim(),
            gameId: item.id,
            imageId: item.imageId,
            customBody: combinedBody
        });
    }, [buildItemBody]);

    // Generate VFM links for all items
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleGenerateVfmLinks = useCallback(() => {
        if (!geeklistId.trim()) {
            setError('Please enter a Geeklist ID for the Virtual Flea Market');
            return;
        }
        
        // Save geeklistId to session storage
        sellSessionStorage.saveGeeklistId(geeklistId);
        
        const updatedItems = sellerItems.map(item => ({
            ...item,
            vfmLink: generateItemVfmLink(item, geeklistId)
        }));
        
        setSellerItems(updatedItems);
        sellSessionStorage.saveSellerItems(updatedItems);
        setIsActionsPanelOpen(false);
    }, [sellerItems, geeklistId, generateItemVfmLink, setIsActionsPanelOpen]);

    // Clear VFM links from all items
    const clearVfmLinks = useCallback(() => {
        if (sellerItems.length > 0 && sellerItems.some(item => item.vfmLink)) {
            const updatedItems = sellerItems.map(item => ({
                ...item,
                vfmLink: null
            }));
            setSellerItems(updatedItems);
        }
    }, [sellerItems]);

    // Recalculate prices when percentage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (sellerItems.length > 0 && sellerItems[0].averagePrice !== null) {
            const updatedItems = sellerItems.map(item => ({
                ...item,
                calculatedPrice: calculateRecommendedPrice(item.averagePrice, pricePercentage),
                userPrice: roundPrice(calculateRecommendedPrice(item.averagePrice, pricePercentage)),
                binPrice: (item.priceType === PRICE_TYPES.STARTING_BID_WITH_BIN || item.priceType === PRICE_TYPES.SOFT_RESERVE_WITH_BIN) ? roundPrice(calculateRecommendedPrice(item.averagePrice, pricePercentage) * defaultBinMultiplier) : null,
                vfmLink: null // Clear VFM links when prices change
            }));
            setSellerItems(updatedItems);
            sellSessionStorage.saveSellerItems(updatedItems);
        }
    }, [pricePercentage, roundPrice, defaultBinMultiplier]);

    // Clear VFM links when rounding settings change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        clearVfmLinks();
    }, [roundingMode, roundingIncrement]);

    // Update all items when default price type changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (sellerItems.length > 0) {
            const updatedItems = sellerItems.map(item => ({
                ...item,
                priceType: defaultPriceType,
                binPrice: (defaultPriceType === PRICE_TYPES.STARTING_BID_WITH_BIN || defaultPriceType === PRICE_TYPES.SOFT_RESERVE_WITH_BIN) && item.userPrice 
                    ? roundPrice(item.userPrice * defaultBinMultiplier) 
                    : null,
                vfmLink: null // Clear VFM links when price type changes
            }));
            setSellerItems(updatedItems);
            sellSessionStorage.saveSellerItems(updatedItems);
        }
    }, [defaultPriceType]);

    // Save state changes to session storage
    useEffect(() => {
        sellSessionStorage.saveUsername(username);
    }, [username]);

    useEffect(() => {
        sellSessionStorage.saveGeeklistId(geeklistId);
    }, [geeklistId]);

    useEffect(() => {
        sellSessionStorage.savePricePercentage(pricePercentage);
    }, [pricePercentage]);

    useEffect(() => {
        sellSessionStorage.saveRoundingMode(roundingMode);
    }, [roundingMode]);

    useEffect(() => {
        sellSessionStorage.saveRoundingIncrement(roundingIncrement);
    }, [roundingIncrement]);

    useEffect(() => {
        sellSessionStorage.saveIsConfigPanelOpen(isConfigPanelOpen);
    }, [isConfigPanelOpen]);

    useEffect(() => {
        sellSessionStorage.saveIsActionsPanelOpen(isActionsPanelOpen);
    }, [isActionsPanelOpen]);

    useEffect(() => {
        sellSessionStorage.saveIsCollectionParamsOpen(isCollectionParamsOpen);
    }, [isCollectionParamsOpen]);

    useEffect(() => {
        sellSessionStorage.saveDefaultPriceType(defaultPriceType);
    }, [defaultPriceType]);

    useEffect(() => {
        sellSessionStorage.saveDefaultBinMultiplier(defaultBinMultiplier);
    }, [defaultBinMultiplier]);

    useEffect(() => {
        sellSessionStorage.saveGlobalBbCode(globalBbCode);
    }, [globalBbCode]);

    useEffect(() => {
        sellSessionStorage.saveCollectionParams(collectionParams);
    }, [collectionParams]);

    useEffect(() => {
        sellSessionStorage.saveIsLoadingCollection(isLoadingCollection);
    }, [isLoadingCollection]);

    useEffect(() => {
        sellSessionStorage.saveLoadingMessage(loadingMessage);
    }, [loadingMessage]);

    useEffect(() => {
        sellSessionStorage.saveIsLoadingPrices(isLoadingPrices);
    }, [isLoadingPrices]);

    useEffect(() => {
        sellSessionStorage.savePriceProgress(priceProgress);
    }, [priceProgress]);

    // Copy BBCode list to clipboard
    const handleCopyToClipboard = useCallback(() => {
        const bbCode = sellerItems
            .filter(item => item.userPrice > 0)
            .map(item => {
                let text = `[url=${BGG_BASE_URL}/boardgame/${item.id}]${item.name}[/url]`;
                
                // Add price based on type
                if (item.priceType === PRICE_TYPES.FIXED_PRICE) {
                    text += ` - $${item.userPrice.toFixed(2)}`;
                } else if (item.priceType === PRICE_TYPES.STARTING_BID) {
                    text += ` - SB: $${item.userPrice.toFixed(2)}`;
                } else if (item.priceType === PRICE_TYPES.STARTING_BID_WITH_BIN) {
                    text += ` - SB: $${item.userPrice.toFixed(2)}`;
                    if (item.binPrice) {
                        text += ` BIN: $${item.binPrice.toFixed(2)}`;
                    }
                } else if (item.priceType === PRICE_TYPES.SOFT_RESERVE) {
                    text += ` - SR: $${item.userPrice.toFixed(2)}`;
                } else if (item.priceType === PRICE_TYPES.FIXED_PRICE_WITH_BIN) {
                    text += ` - FP: $${item.userPrice.toFixed(2)}`;
                    if (item.binPrice) {
                        text += ` BIN: $${item.binPrice.toFixed(2)}`;
                    }
                } else if (item.priceType === PRICE_TYPES.BUY_IT_NOW) {
                    text += ` - BIN: $${item.userPrice.toFixed(2)}`;
                }
                
                if (item.versionName) {
                    text += ` (${item.versionName})`;
                }
                return text;
            })
            .join('\n');
        
        // Fallback for browsers that don't support clipboard API (e.g., mobile, non-HTTPS)
        const fallbackCopyToClipboard = (text) => {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            
            // Make the textarea out of viewport
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            textArea.style.top = '-9999px';
            
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);
                if (successful) {
                    alert('List copied to clipboard!');
                } else {
                    alert('Failed to copy. Please try again.');
                }
            } catch (err) {
                document.body.removeChild(textArea);
                console.error('Fallback copy failed:', err);
                alert('Failed to copy. Please try again.');
            }
        };
        
        // Try modern clipboard API first
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard.writeText(bbCode).then(() => {
                alert('List copied to clipboard!');
            }).catch(err => {
                console.error('Clipboard API failed, trying fallback:', err);
                fallbackCopyToClipboard(bbCode);
            });
        } else {
            // Use fallback for browsers without clipboard API support
            fallbackCopyToClipboard(bbCode);
        }

        if(isActionsPanelOpen) setIsActionsPanelOpen(false);
    }, [sellerItems]);

    // Custom cell renderers
    const GameNameRenderer = useCallback((params) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {params.data.thumbnail && (
                    <img 
                        src={params.data.thumbnail} 
                        alt={params.data.name}
                        style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                )}
                <div style={{ textAlign: 'left' }}>
                    <a 
                        href={`${BGG_BASE_URL}/boardgame/${params.data.id}`} 
                        target="_blank" 
                        rel="noreferrer"
                        style={{ color: '#ff9900', textDecoration: 'none' }}
                    >
                        {params.data.name}
                    </a>
                    {params.data.yearPublished && (
                        <span style={{ color: '#666', fontSize: '0.85em', marginLeft: '4px' }}>
                            ({params.data.yearPublished})
                        </span>
                    )}
                </div>
            </div>
        );
    }, []);

    const PriceRenderer = useCallback((params) => {
        if (params.data.isLoadingPrice) {
            return <div className="loader"></div>;
        }
        if (params.data.averagePrice === null) {
            return <span style={{ color: '#aaa' }}>Not loaded</span>;
        }
        if (params.data.averagePrice === 0) {
            return <span style={{ color: '#999' }}>No sales data</span>;
        }
        return (
            <a 
                href={`${BGG_MARKETPLACE_URL}/${params.data.id}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#ff9900', textDecoration: 'none' }}
            >
                ${formatPrice(params.data.averagePrice)}
            </a>
        );
    }, []);

    const PriceTypeRenderer = useCallback((params) => {
        const handleChange = (e) => {
            const newPriceType = e.target.value;
            params.data.priceType = newPriceType;
            
            if ((newPriceType === PRICE_TYPES.STARTING_BID_WITH_BIN || newPriceType === PRICE_TYPES.SOFT_RESERVE_WITH_BIN) && params.data.userPrice) {
                params.data.binPrice = roundPrice(params.data.userPrice * defaultBinMultiplier);
            } else {
                params.data.binPrice = null;
            }
            params.data.vfmLink = null;
            
            params.api.refreshCells({ 
                rowNodes: [params.node], 
                columns: ['binPrice', 'vfmLink'] 
            });
        };
        
        return (
            <select
                defaultValue={params.data.priceType || PRICE_TYPES.FIXED_PRICE}
                onChange={handleChange}
                style={{ 
                    width: '100%', 
                    padding: '4px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#fff'
                }}
            >
                <option value="FP">FP</option>
                <option value="SB">SB</option>
                <option value="BIN">BIN</option>
                <option value="SB+BIN">SB+BIN</option>
                <option value="SR">SR</option>
                <option value="SR+BIN">SR+BIN</option>
            </select>
        );
    }, [defaultBinMultiplier, roundPrice]);

    const UserPriceRenderer = useCallback((params) => {
        const handleBlur = (e) => {
            const newPrice = parseFloat(e.target.value) || 0;
            params.data.userPrice = newPrice;
            
            if (params.data.priceType === PRICE_TYPES.STARTING_BID_WITH_BIN || params.data.priceType === PRICE_TYPES.SOFT_RESERVE_WITH_BIN) {
                params.data.binPrice = roundPrice(newPrice * defaultBinMultiplier);
            }
            params.data.vfmLink = null;
            
            params.api.refreshCells({ 
                rowNodes: [params.node], 
                columns: ['binPrice', 'vfmLink'] 
            });
        };
        
        return (
            <input
                type="number"
                defaultValue={params.data.userPrice || ''}
                onBlur={handleBlur}
                style={{ 
                    width: '80px', 
                    padding: '4px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                }}
                step="0.01"
                min="0"
            />
        );
    }, [defaultBinMultiplier, roundPrice]);

    const BinPriceRenderer = useCallback((params) => {
        if (params.data.priceType !== PRICE_TYPES.STARTING_BID_WITH_BIN && params.data.priceType !== PRICE_TYPES.SOFT_RESERVE_WITH_BIN) {
            return <span style={{ color: '#ccc' }}>-</span>;
        }
        
        const handleBlur = (e) => {
            params.data.binPrice = parseFloat(e.target.value) || 0;
            params.data.vfmLink = null;
            params.api.refreshCells({ 
                rowNodes: [params.node], 
                columns: ['vfmLink'] 
            });
        };
        
        return (
            <input
                type="number"
                defaultValue={params.data.binPrice || ''}
                onBlur={handleBlur}
                style={{ 
                    width: '80px', 
                    padding: '4px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                }}
                step="0.01"
                min="0"
            />
        );
    }, []);

    const VersionRenderer = useCallback((params) => {
        const handleBlur = (e) => {
            params.data.versionName = e.target.value;
            params.data.vfmLink = null;
            params.api.refreshCells({ 
                rowNodes: [params.node], 
                columns: ['vfmLink'] 
            });
        };
        
        return (
            <input
                type="text"
                defaultValue={params.data.versionName || ''}
                onBlur={handleBlur}
                placeholder="Enter version"
                style={{ 
                    width: '100%', 
                    padding: '4px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                }}
            />
        );
    }, []);

    const VfmLinkRenderer = useCallback((params) => {
        if (!params.data.vfmLink) {
            const handleGenerateSingleLink = () => {
                if (geeklistId && geeklistId.trim()) {
                    // Generate VFM link for this single item
                    const vfmLink = generateItemVfmLink(params.data, geeklistId);
                    
                    // Update just this row with the generated link
                    const updatedItem = { ...params.data, vfmLink };
                    const updatedItems = sellerItems.map(item => 
                        item.id === params.data.id ? updatedItem : item
                    );
                    setSellerItems(updatedItems);
                    sellSessionStorage.saveSellerItems(updatedItems);
                } else {
                    // No geeklistId, open the panel
                    setIsActionsPanelOpen(true);
                }
            };
            
            return <button 
                className='link-button'
                onClick={handleGenerateSingleLink}
                style={{ fontStyle: 'italic' }}
            >
                Generate Link
            </button>;
        }
        return (
            <a 
                href={params.data.vfmLink}
                target="_blank"
                rel="noreferrer"
                style={{ 
                    color: '#ff9900', 
                    textDecoration: 'none',
                    fontWeight: '500'
                }}
            >
                Add to VFM
            </a>
        );
    }, [geeklistId, sellerItems, setSellerItems, setIsActionsPanelOpen, generateItemVfmLink]);

    const BodyRenderer = useCallback((params) => {
        const handleBlur = (e) => {
            params.data.customBody = e.target.value;
            params.data.vfmLink = null;
            params.api.refreshCells({ 
                rowNodes: [params.node], 
                columns: ['vfmLink'] 
            });
        };
        
        return (
            <input
                type="text"
                defaultValue={params.data.customBody || ''}
                onBlur={handleBlur}
                placeholder="Listing description"
                style={{ 
                    width: '100%', 
                    padding: '4px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                }}
            />
        );
    }, []);

    const ConditionRenderer = useCallback((params) => {
        const handleChange = (e) => {
            const newCondition = e.target.value;
            params.data.condition = newCondition;
            params.data.vfmLink = null;
            
            // Refresh the cell to update the dropdown display
            params.api.refreshCells({ 
                rowNodes: [params.node], 
                columns: ['condition', 'vfmLink'] 
            });
        };
        
        return (
            <select
                key={params.data.condition + '-' + params.node.id}
                value={params.data.condition || 'Very Good'}
                onChange={handleChange}
                title={CONDITION_OPTIONS.find(opt => opt.value === params.data.condition)?.label || 'Select condition'}
                style={{ 
                    width: '100%', 
                    padding: '4px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    backgroundColor: '#fff'
                }}
            >
                {CONDITION_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.value || 'Select...'}
                    </option>
                ))}
            </select>
        );
    }, []);

    const ConditionCommentsRenderer = useCallback((params) => {
        const handleBlur = (e) => {
            params.data.conditionComments = e.target.value;
            params.data.vfmLink = null;
            params.api.refreshCells({ 
                rowNodes: [params.node], 
                columns: ['vfmLink'] 
            });
        };
        
        return (
            <input
                type="text"
                defaultValue={params.data.conditionComments || ''}
                onBlur={handleBlur}
                placeholder="Condition notes"
                style={{ 
                    width: '100%', 
                    padding: '4px 8px',
                    fontSize: '14px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                }}
            />
        );
    }, []);

    // Column definitions
    const columnDefs = [
        {
            headerName: 'Game',
            field: 'name',
            cellRenderer: GameNameRenderer,
            minWidth: 250,
            flex: 2,
            sortable: true,
            filter: true
        },
        {
            headerName: 'Avg Price',
            field: 'averagePrice',
            cellRenderer: PriceRenderer,
            width: 100,
            sortable: true
        },
        {
            headerName: 'Calculated',
            field: 'calculatedPrice',
            valueFormatter: (params) => params.value ? `$${formatPrice(params.value)}` : '-',
            width: 110,
            sortable: true
        },
        {
            headerName: 'Type',
            field: 'priceType',
            cellRenderer: PriceTypeRenderer,
            width: 140
        },
        {
            headerName: 'Price',
            field: 'userPrice',
            cellRenderer: UserPriceRenderer,
            width: 100
        },
        {
            headerName: 'BIN',
            field: 'binPrice',
            cellRenderer: BinPriceRenderer,
            width: 100
        },
        {
            headerName: 'Version',
            field: 'versionName',
            cellRenderer: VersionRenderer,
            minWidth: 150,
            flex: 1
        },
        {
            headerName: 'Condition',
            field: 'condition',
            cellRenderer: ConditionRenderer,
            width: 140
        },
        {
            headerName: 'Condition Notes',
            field: 'conditionComments',
            cellRenderer: ConditionCommentsRenderer,
            minWidth: 150,
            flex: 1
        },
        {
            headerName: 'Notes',
            field: 'customBody',
            cellRenderer: BodyRenderer,
            minWidth: 150,
            flex: 1
        },
        {
            headerName: 'VFM Link',
            field: 'vfmLink',
            cellRenderer: VfmLinkRenderer,
            width: 135
        }
    ];

    return (
        <div className="app-container">
            <div className="controls-top-bar sell-controls">
                <div className="sell-controls-row">
                    <div className="sell-username-input">
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Enter your BGG username"
                            className="control-input"
                            onKeyDown={(e) => e.key === 'Enter' && handleLoadCollection()}
                        />
                    </div>
                    <button 
                        onClick={handleLoadCollection}
                        disabled={isLoadingCollection}
                        className="control-button primary-button sell-load-btn"
                    >
                        {isLoadingCollection ? 'Loading...' : 'Load Collection'}
                    </button>
                </div>

                {error && (
                    <div className="sell-error-message">
                        {error}
                    </div>
                )}
            </div>

            {isLoadingCollection && (
                <LoadingInline message={loadingMessage} type="spinner" />
            )}

            {isLoadingPrices && (
                <LoadingInline 
                    message={`Loading prices: ${priceProgress.current}/${priceProgress.total}`} 
                    progress={(priceProgress.current / priceProgress.total) * 100}
                    type="progress" 
                />
            )}

            {!isLoadingCollection && (
                <>
                    <div className="table-info sell-table-info">
                        <div className="sell-table-info-top">
                            <div className="sell-action-icons">
                                <button 
                                    className="icon-button" 
                                    onClick={() => setIsCollectionParamsOpen(true)}
                                    title="Collection Parameters"
                                >
                                    <AiOutlineSetting />
                                </button>
                                <button 
                                    className="icon-button" 
                                    onClick={handleLoadPrices}
                                    disabled={isLoadingPrices || sellerItems.length === 0}
                                    title="Load Prices"
                                >
                                    <AiOutlineThunderbolt />
                                </button>
                                <button 
                                    className="icon-button" 
                                    onClick={() => setIsActionsPanelOpen(true)}
                                    disabled={sellerItems.length === 0}
                                    title="VFM Actions"
                                >
                                    <AiOutlineLink />
                                </button>
                                {/* <button 
                                    className="icon-button" 
                                    onClick={handleCopyToClipboard}
                                    disabled={sellerItems.length === 0}
                                    title="Copy List"
                                >
                                    <AiOutlineCopy />
                                </button> */}
                                <button 
                                    className="icon-button sell-settings-btn" 
                                    onClick={() => setIsConfigPanelOpen(true)}
                                    title="Price Settings"
                                >
                                    <AiOutlineControl />
                                </button>
                            </div>
                        </div>
                    </div>
    
                    {sellerItems.length > 0 && (
                        <div className="table-container">
                            <div className="ag-theme-alpine" style={{ height: 'calc(100vh - 275px)', width: '100%' }}>
                                <AgGridReact
                                    ref={gridRef}
                                    columnDefs={columnDefs}
                                    rowData={sellerItems}
                                    className="data-grid"
                                    rowHeight={56}
                                    animateRows={true}
                                    suppressRowTransform={true}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}

            {sellerItems.length === 0 && !isLoadingCollection && !error && (
                <div className="no-geeklist-message">
                    <p>To get started, enter your BoardGameGeek username and click "Load Collection", or adjust collection parameters (<button 
                                    className="icon-button" 
                                    onClick={() => setIsCollectionParamsOpen(true)}
                                    title="Collection Parameters"
                                >
                                    <AiOutlineSetting />
                                </button>) first.</p>
                    <p>Click the about button <AiOutlineInfoCircle style={{fontSize: 'large'}} /> for more information.</p>
                </div>
            )}

            {/* Collection Parameters Slide Panel */}
            <CollectionParameters
                isOpen={isCollectionParamsOpen}
                onClose={() => setIsCollectionParamsOpen(false)}
                onApply={(newParams) => {
                    setCollectionParams(newParams);
                    // Clear existing items when parameters change
                    setSellerItems([]);
                }}
                currentParams={collectionParams}
            />

            {/* Price Settings Slide Panel */}
            <SlidePanel 
                isOpen={isConfigPanelOpen} 
                onClose={() => setIsConfigPanelOpen(false)} 
                title="Seller Settings"
                width="360px"
            >
                {/* Default Price Type */}
                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Default Price Type</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="defaultPriceType"
                                value={PRICE_TYPES.FIXED_PRICE}
                                checked={defaultPriceType === PRICE_TYPES.FIXED_PRICE}
                                onChange={() => setDefaultPriceType(PRICE_TYPES.FIXED_PRICE)}
                            />
                            <span>{PRICE_TYPES.FIXED_PRICE} - Fixed Price</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="defaultPriceType"
                                value={PRICE_TYPES.STARTING_BID}
                                checked={defaultPriceType === PRICE_TYPES.STARTING_BID}
                                onChange={() => setDefaultPriceType(PRICE_TYPES.STARTING_BID)}
                            />
                            <span>{PRICE_TYPES.STARTING_BID} - Starting Bid Only</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="defaultPriceType"
                                value={PRICE_TYPES.STARTING_BID_WITH_BIN}
                                checked={defaultPriceType === PRICE_TYPES.STARTING_BID_WITH_BIN}
                                onChange={() => setDefaultPriceType(PRICE_TYPES.STARTING_BID_WITH_BIN)}
                            />
                            <span>{PRICE_TYPES.STARTING_BID_WITH_BIN} - Starting Bid with Buy It Now</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="defaultPriceType"
                                value={PRICE_TYPES.SOFT_RESERVE}
                                checked={defaultPriceType === PRICE_TYPES.SOFT_RESERVE}
                                onChange={() => setDefaultPriceType(PRICE_TYPES.SOFT_RESERVE)}
                            />
                            <span>{PRICE_TYPES.SOFT_RESERVE} - Soft Reserve</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="defaultPriceType"
                                value={PRICE_TYPES.SOFT_RESERVE_WITH_BIN}
                                checked={defaultPriceType === PRICE_TYPES.SOFT_RESERVE_WITH_BIN}
                                onChange={() => setDefaultPriceType(PRICE_TYPES.SOFT_RESERVE_WITH_BIN)}
                            />
                            <span>{PRICE_TYPES.SOFT_RESERVE_WITH_BIN} - Soft Reserve with Buy It Now</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="defaultPriceType"
                                value={PRICE_TYPES.BUY_IT_NOW}
                                checked={defaultPriceType === PRICE_TYPES.BUY_IT_NOW}
                                onChange={() => setDefaultPriceType(PRICE_TYPES.BUY_IT_NOW)}
                            />
                            <span>{PRICE_TYPES.BUY_IT_NOW} - Buy It Now Only</span>
                        </label>
                    </div>
                </div>

                {/* Price Percentage */}
                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Price Percentage (%)</label>
                    <div className="control-input-group">
                        <input
                            type="number"
                            value={pricePercentage}
                            onChange={(e) => setPricePercentage(parseInt(e.target.value) || 0)}
                            min="1"
                            max="150"
                            style={{ width: '80px' }}
                            className="control-input"
                        />
                        <span style={{ color: '#666', fontSize: '0.85em' }}>
                            of average market price
                        </span>
                    </div>
                </div>

                {/* BIN Multiplier - Always visible */}
                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>BIN Multiplier</label>
                    <div className="control-input-group">
                        <input
                            type="number"
                            value={defaultBinMultiplier}
                            onChange={(e) => setDefaultBinMultiplier(parseFloat(e.target.value) || 1)}
                            min="1"
                            max="3"
                            step="0.1"
                            style={{ width: '80px' }}
                            className="control-input"
                        />
                        <span style={{ color: '#666', fontSize: '0.85em' }}>
                            × price (e.g., 1.2 = 20% higher) - used for SB+BIN items
                        </span>
                    </div>
                </div>

                {/* Rounding Mode */}
                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Rounding Mode</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="roundingMode"
                                value="none"
                                checked={roundingMode === 'none'}
                                onChange={() => setRoundingMode('none')}
                            />
                            No Rounding
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="roundingMode"
                                value="nearest"
                                checked={roundingMode === 'nearest'}
                                onChange={() => setRoundingMode('nearest')}
                            />
                            Round to Nearest
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="roundingMode"
                                value="up"
                                checked={roundingMode === 'up'}
                                onChange={() => setRoundingMode('up')}
                            />
                            Round Up
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="roundingMode"
                                value="down"
                                checked={roundingMode === 'down'}
                                onChange={() => setRoundingMode('down')}
                            />
                            Round Down
                        </label>
                    </div>
                </div>

                {/* Rounding Increment */}
                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Rounding Increment</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="roundingIncrement"
                                value="5"
                                checked={roundingIncrement === 5}
                                onChange={() => setRoundingIncrement(5)}
                            />
                            $5.00
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="roundingIncrement"
                                value="1"
                                checked={roundingIncrement === 1}
                                onChange={() => setRoundingIncrement(1)}
                            />
                            $1.00
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="roundingIncrement"
                                value="0.5"
                                checked={roundingIncrement === 0.5}
                                onChange={() => setRoundingIncrement(0.5)}
                            />
                            $0.50
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', textAlign: 'left' }}>
                            <input
                                type="radio"
                                name="roundingIncrement"
                                value="0.1"
                                checked={roundingIncrement === 0.1}
                                onChange={() => setRoundingIncrement(0.1)}
                            />
                            $0.10
                        </label>
                    </div>
                </div>

                {/* Tips */}
                <div style={{ padding: '16px', fontSize: '0.85em', color: '#666', backgroundColor: '#f9f9f9' }}>
                    <p style={{ margin: 0 }}>
                        <strong>Tip:</strong> Set your preferences before or after loading prices. 
                        Price type can be changed per item in the grid.
                    </p>
                </div>
            </SlidePanel>

            {/* VFM Actions Slide Panel */}
            <SlidePanel 
                isOpen={isActionsPanelOpen} 
                onClose={() => setIsActionsPanelOpen(false)} 
                title="VFM Link Generation Settings"
                width="360px"
            >
                {/* Geeklist ID Input */}
                <div>
                    <label style={{ fontWeight: '500', marginTop: '8px', marginBottom: '8px', display: 'block' }}>Geeklist ID</label>
                    <input
                        type="text"
                        value={geeklistId}
                        onChange={(e) => setGeeklistId(e.target.value)}
                        placeholder="Enter VFM ID (e.g., 370282)"
                        className="control-input"
                        style={{ width: '100%' }}
                        disabled={sellerItems.length === 0}
                    />
                    <p style={{ margin: '8px 0 0', fontSize: '0.85em', color: '#666' }}>
                        Find the ID in the VFM URL: boardgamegeek.com/geeklist/<strong>370282</strong>
                    </p>
                </div>

                {/* Global BBCode Input */}
                <div>
                    <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>Global BBCode</label>
                    <textarea
                        value={globalBbCode}
                        onChange={(e) => setGlobalBbCode(e.target.value)}
                        placeholder="Enter BBCode to include in all listings (e.g., [B]Condition:[/B] Like new)"
                        className="control-input"
                        style={{ width: '100%', minHeight: '80px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }}
                        disabled={sellerItems.length === 0}
                    />
                    <p style={{ margin: '8px 0 0', fontSize: '0.85em', color: '#666' }}>
                        This BBCode will be prepended to each item's listing description.
                    </p>
                </div>

                {/* Generate Links Button */}
                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <button 
                        onClick={handleGenerateVfmLinks}
                        disabled={!geeklistId.trim() || sellerItems.length === 0}
                        className="control-button primary-button"
                        style={{ width: '100%' }}
                    >
                        <AiOutlineLink style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Generate VFM Links
                    </button>
                    <p style={{ margin: '8px 0 0', fontSize: '0.85em', color: '#666' }}>
                        This will create "Add to VFM" links for each item in your list.
                    </p>
                </div>

                {/* Copy List Button */}
                <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                    <button 
                        onClick={handleCopyToClipboard}
                        disabled={sellerItems.length === 0}
                        className="control-button"
                        style={{ width: '100%' }}
                    >
                        <AiOutlineCopy style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Copy List to Clipboard
                    </button>
                    <p style={{ margin: '8px 0 0', fontSize: '0.85em', color: '#666' }}>
                        Copies BBCode formatted list with game names and prices.
                    </p>
                </div>

                {/* Status */}
                <div style={{ padding: '16px', fontSize: '0.85em', color: '#666', backgroundColor: '#f9f9f9' }}>
                    <p style={{ margin: 0 }}>
                        <strong>Status:</strong> {sellerItems.filter(i => i.vfmLink).length} of {sellerItems.length} items have VFM links generated.
                    </p>
                </div>
            </SlidePanel>
        </div>
    );
};

export default SellerList;
